import { latLng, type LatLngTuple } from 'leaflet';
import type { MoTravelDay, MoTravelRoadLeg, MoTravelStop } from '../types/moTravel.types';
import { isLocated } from './dayRoutes';
import { formatKmValue } from './formatKm';
import { minutesBetween } from './minutesBetween';
import { getStopKey } from './stopKey';

const LONG_GAP_MINUTES = 60;
const LATE_SUBMISSION_MINUTES = 120;
const METERS_PER_KM = 1000;
const SAME_SPOT_KM = 0.05;

export interface StopInsight {
  distanceKm: number | null;
  gapMinutes: number | null;
  isLongGap: boolean;
  lateMinutes: number | null;
}

const distanceKmBetween = (from: LatLngTuple, to: LatLngTuple): number =>
  latLng(from).distanceTo(to) / METERS_PER_KM;

const toStopPosition = (stop: MoTravelStop): LatLngTuple | null =>
  isLocated(stop) ? [stop.lat, stop.lon] : null;

function getMatchingRoadLegs(day: MoTravelDay): MoTravelRoadLeg[] | null {
  const routePointCount = (day.start ? 1 : 0) + day.stops.filter(isLocated).length;
  return day.roadLegs?.length === routePointCount - 1 ? day.roadLegs : null;
}

export function buildStopInsights(day: MoTravelDay): Map<string, StopInsight> {
  const insights = new Map<string, StopInsight>();
  const roadLegs = getMatchingRoadLegs(day);
  let previousPosition: LatLngTuple | null = day.start ? [day.start.lat, day.start.lon] : null;
  let passedRoutePointCount = previousPosition ? 1 : 0;
  let previousEndTime: string | null = day.startTime;

  for (const stop of day.stops) {
    const stopPosition = toStopPosition(stop);
    const gapMinutes = minutesBetween(previousEndTime, stop.timeFrom);
    const lateMinutes = minutesBetween(stop.timeTo, stop.submittedAt);
    const roadLegKm = roadLegs?.[passedRoutePointCount - 1]?.km;

    insights.set(getStopKey(day, stop), {
      distanceKm:
        stopPosition && previousPosition
          ? (roadLegKm ?? distanceKmBetween(previousPosition, stopPosition))
          : null,
      gapMinutes: gapMinutes !== null && gapMinutes >= 0 ? gapMinutes : null,
      isLongGap: gapMinutes !== null && gapMinutes > LONG_GAP_MINUTES,
      lateMinutes:
        lateMinutes !== null && lateMinutes > LATE_SUBMISSION_MINUTES ? lateMinutes : null,
    });

    if (stopPosition) {
      previousPosition = stopPosition;
      passedRoutePointCount += 1;
    }
    previousEndTime = stop.timeTo ?? stop.submittedAt ?? previousEndTime;
  }

  return insights;
}

export const formatLegDistance = (distanceKm: number): string =>
  distanceKm < SAME_SPOT_KM ? 'Same spot' : `${formatKmValue(distanceKm)} km`;

function countStopAlerts(stop: MoTravelStop, insight: StopInsight | undefined): number {
  const alerts = [!stop.hasLocation, insight?.isLongGap === true, insight?.lateMinutes != null];
  return alerts.filter(Boolean).length;
}

export function countDayAlerts(day: MoTravelDay, insights: Map<string, StopInsight>): number {
  return day.stops.reduce(
    (alertCount, stop) => alertCount + countStopAlerts(stop, insights.get(getStopKey(day, stop))),
    0,
  );
}
